// The day-as-artifact ribbon. Past = solid colored bars, upcoming = dashed
// outline, now = vertical white line with glow dot, active event = soft glow.

import { hmShort } from "../lib/timeFmt";
import type { DayPlan, PlanEvent } from "../lib/dayPlan";
import { useT } from "../lib/LocaleContext";

type Props = {
  plan: DayPlan;
  activeId?: string | null;
};

export function DayRibbon({ plan, activeId }: Props) {
  const t = useT();
  const { dayStartMin, dayEndMin, nowMin, events } = plan;
  const total = Math.max(1, dayEndMin - dayStartMin);
  const pct = (m: number) => ((m - dayStartMin) / total) * 100;

  // Hour ticks every 2 hours.
  const firstTick = Math.ceil(dayStartMin / 60 / 2) * 2 * 60;
  const ticks: number[] = [];
  for (let t = firstTick; t <= dayEndMin; t += 120) ticks.push(t);

  return (
    <div style={styles.wrap}>
      <div style={styles.headRow}>
        <span style={styles.headLabel}>{t.dayRibbon.today}</span>
        <span className="bh-num" style={styles.headSub}>
          {t.dayRibbon.span(hmShort(dayStartMin), hmShort(dayEndMin), Math.round(total / 60))}
        </span>
      </div>

      <div style={styles.trackWrap}>
        <div style={styles.track}>
          {ticks.map((t) => (
            <div key={`tick-${t}`} style={{ ...styles.tick, left: `${pct(t)}%` }} />
          ))}

          {events.map((e) => renderSegment(e, pct, total, activeId))}

          <div style={{ ...styles.nowMark, left: `${pct(nowMin)}%` }}>
            <div style={styles.nowDot} />
          </div>
        </div>

        <div style={styles.tickLabels}>
          {ticks.map((t) => (
            <span
              key={`label-${t}`}
              className="bh-num"
              style={{
                position: "absolute",
                left: `${pct(t)}%`,
                transform: "translateX(-50%)",
                fontSize: 10,
                color: "var(--bh-ink-4)",
              }}
            >
              {hmShort(t)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function renderSegment(e: PlanEvent, pct: (m: number) => number, total: number, activeId?: string | null) {
  // Skip plain wake events on the ribbon (just dot in time, not a span).
  if (e.kind === "wake") return null;

  const isPast = e.status === "logged";
  const isLive = e.status === "live" || e.id === activeId;
  const start = e.status === "upcoming" ? e.predicted?.start ?? e.start : e.start;
  const dur =
    isPast || isLive
      ? Math.max(2, (e.end ?? e.start + 8) - e.start)
      : e.predicted?.dur ?? (e.kind === "feed" || e.kind === "nurse" ? 18 : e.kind === "bedtime" ? 30 : 70);
  const w = Math.max(1.4, (dur / total) * 100);

  const color =
    e.kind === "feed" || e.kind === "nurse"
      ? "var(--bh-feed)"
      : e.kind === "sleep" || e.kind === "bedtime"
        ? "var(--bh-sleep)"
        : "var(--bh-ink-3)";

  return (
    <div
      key={e.id}
      title={`${e.kind} @ ${hmShort(start)}`}
      style={{
        position: "absolute",
        left: `${pct(start)}%`,
        width: `${w}%`,
        top: 6,
        bottom: 6,
        background: isPast || isLive ? color : "transparent",
        border: isPast || isLive ? "none" : `1.5px dashed ${color}`,
        opacity: isPast ? 1 : isLive ? 1 : 0.7,
        borderRadius: 4,
        boxShadow: isLive ? `0 0 0 3px color-mix(in oklch, ${color} 30%, transparent)` : "none",
      }}
    />
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    margin: "0 14px 18px",
    padding: "14px 18px 12px",
    background: "var(--bh-surface)",
    borderRadius: 22,
  },
  headRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  headLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
  },
  headSub: { fontSize: 11, color: "var(--bh-ink-4)" },
  trackWrap: { position: "relative", paddingBottom: 16 },
  track: {
    position: "relative",
    height: 32,
    background: "var(--bh-surface-press)",
    borderRadius: 6,
  },
  tick: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 1,
    background: "var(--bh-divider)",
    transform: "translateX(-50%)",
    pointerEvents: "none",
  },
  tickLabels: { position: "absolute", left: 0, right: 0, top: 36, height: 14 },
  nowMark: {
    position: "absolute",
    top: -6,
    bottom: -6,
    width: 2,
    background: "var(--bh-now)",
    transform: "translateX(-50%)",
    borderRadius: 2,
    zIndex: 5,
  },
  nowDot: {
    position: "absolute",
    top: -5,
    left: "50%",
    transform: "translateX(-50%)",
    width: 10,
    height: 10,
    borderRadius: 999,
    background: "var(--bh-now)",
    boxShadow: "0 0 0 3px color-mix(in oklch, var(--bh-now) 30%, transparent)",
  },
};
